/* eslint-disable max-lines */
import './DataGridTable.scss';

import { DataGrid, GridColDef, GridFeatureMode, GridFilterModel, GridFooterContainerProps, GridRowSelectionModel } from '@mui/x-data-grid';
import React, { useEffect, useRef, useState } from 'react';

import { emitSendEmailOpen } from '../../../constants/events';
import NotFoundWidget from "../../CopTable/widget/NotFoundWidget";
import DataGridActions from './DataGridActions';

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <span className="cop-legend-dot">
    <span className="cop-legend-dot__circle" style={{ backgroundColor: color }} />{label}
  </span>
);
type LegendItem = { color: string; label: string };
type CustomFooterProps = GridFooterContainerProps & { message?: string; count?: number };
const CustomFooter = (props: CustomFooterProps) => {
  const displayCount = props.count ?? 0;
  return <div className="cop-footer__count">{props.message ?? 'Count'}: {displayCount}</div>;
};
const SpinnerOverlay = () => (
  <div className="cop-data-grid__spinner-overlay"><div className="cop-data-grid__spinner" /></div>
);

const buildLocaleText = (labels?: ColumnActionLabels, noRowsLabel?: string) => ({
  ...(labels?.sortAsc && { columnMenuSortAsc: labels.sortAsc }),
  ...(labels?.sortDesc && { columnMenuSortDesc: labels.sortDesc }),
  ...(labels?.unsort && { columnMenuUnsort: labels.unsort }),
  ...(labels?.filter && { columnMenuFilter: labels.filter }),
  ...(labels?.hideColumn && { columnMenuHideColumn: labels.hideColumn }),
  ...(labels?.manageColumns && { columnMenuManageColumns: labels.manageColumns }),
  ...(labels?.searchColumns && { columnsManagementSearchTitle: labels.searchColumns }),
  ...(labels?.showHideAll && { columnsManagementShowHideAllText: labels.showHideAll }),
  ...(labels?.resetButton && { columnsManagementReset: labels.resetButton }),
  ...(labels?.filterOperatorLabels?.[0] && { filterOperatorContains: labels.filterOperatorLabels[0] }),
  ...(labels?.filterOperatorLabels?.[1] && { filterOperatorEquals: labels.filterOperatorLabels[1] }),
  ...(labels?.filterOperatorLabels?.[2] && { filterOperatorStartsWith: labels.filterOperatorLabels[2] }),
  noRowsLabel,
  noResultsOverlayLabel: noRowsLabel,
});

const GRID_SX = {
  border: 'none',
  borderTop: '1px solid #d1d5db',
  '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1a202c', borderRadius: 0 },
  '& .MuiDataGrid-columnHeader': { background: '#4D4D4D', borderRight: '1px solid #B3B3B3', '&:last-child': { borderRight: 'none' } },
  '& .MuiDataGrid-columnHeaderTitle': { fontFamily: 'hitachisans-custom, "Hitachi Sans", sans-serif', fontWeight: 700, color: '#fff', fontSize: '14px', '@media (max-width:768px)': { fontSize: '12px' } },
  '& .MuiDataGrid-columnSeparator': { maxWidth: 0 },
  '& .MuiDataGrid-sortIcon': { color: '#fff', opacity: '1 !important' },
  '& .MuiDataGrid-iconButtonContainer': { visibility: 'visible', width: 'auto' },
  '& .MuiDataGrid-columnHeader svg': { color: '#fff', fill: '#fff' },
  '& .MuiDataGrid-columnHeader .MuiButtonBase-root': { color: '#fff', backgroundColor: 'transparent', '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' } },
  '& .MuiDataGrid-columnHeader .MuiTouchRipple-root': { color: 'rgba(255,255,255,0.3)' },
  '& .MuiDataGrid-menuIcon': { visibility: 'hidden', width: 0 },
  '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon': { visibility: 'visible', width: 'auto' },
  '& .MuiDataGrid-row': { borderBottom: '1px solid #e8e8e8', '&:hover': { backgroundColor: '#fafafa' } },
  '& .MuiDataGrid-row.cop-row--my-action': { backgroundColor: '#fdecec', '&:hover': { backgroundColor: '#f1d1d1' } },
  '& .MuiDataGrid-cell': { borderBottom: 'none', borderRight: '1px solid #e8e8e8', fontSize: '12px', color: '#333', '&:last-child': { borderRight: 'none' }, fontFamily: 'hitachisans-custom, "Hitachi Sans", sans-serif' },
  '& .MuiDataGrid-footerContainer': { border: 'none', padding: 0 },
  '& .MuiCheckbox-root.Mui-checked': { color: '#d50000', },
  '& .MuiCheckbox-root.MuiCheckbox-indeterminate': { color: '#d50000', },
  "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root": { color: "rgba(255,255,255,0.7)" },
  "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root.Mui-checked": { color: "#d50000 !important" },
  "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root.Mui-checked svg": { color: "#d50000", backgroundColor: "#d50000", borderRadius: "4px" },
  "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root.MuiCheckbox-indeterminate": { color: "#d50000 !important" },
  "& .MuiDataGrid-columnHeaderCheckbox .MuiCheckbox-root.MuiCheckbox-indeterminate svg": { color: "#d50000", backgroundColor: "#d50000", borderRadius: "4px" },
};

interface ColumnActionLabels {
  sortAsc?: string; sortDesc?: string; unsort?: string; filter?: string;
  hideColumn?: string; manageColumns?: string; searchColumns?: string;
  showHideAll?: string; resetButton?: string; filterOperatorLabels?: string[];
}

interface CopDataGridProps<T extends { id: string | number }> {
  gridType: string; rows: T[]; columns: GridColDef[];
  page: number; pageSize: number; totalCount: number; paginationModeType: GridFeatureMode;
  onPageChange: (page: number) => void; onPageSizeChange: (pageSize: number) => void;
  onSearchChange?: (value: string) => void; searchPlaceholder?: string; itemTypeCountLabel?: string;
  itemsPerPageLabel?: string;
  filterOptions?: { label: string; value: string }[]; selectedFilter?: string; onFilterChange?: (value: string) => void;
  legends?: LegendItem[]; loading?: boolean; noRowsLabel?: string;
  handleFilterChange?: (model: GridFilterModel) => void;
  actionButton?:
  | { type: 'action'; label: string; href: string; enabled: boolean }
  | { type: 'export'; label: string; enabled: boolean; options: { label: string; value: string; onClick: () => void }[] };
  columnActionLabels?: ColumnActionLabels;
  gaShieldToggle?: { label: string; enableGaShieldToggle: boolean | string; checked: boolean; onChange: (checked: boolean) => void };
  selectionActions?: {
    label?: string; enableMultiSelectUsers?: boolean;
    onDelete?: (ids: (string | number)[]) => void; onSelectionChange?: (ids: (string | number)[]) => void;
    notifySelectedUsersLabel?: string; deleteSelectedUsersLabel?: string; noUsersSelectedLabel?: string;
  };
  addActionButton?: { buttonKey: string; buttonType: string; buttonLabel: string; buttonUrl: string };
  onReload?: () => void; isUserManagementLoaded: boolean; resetSearchKey?: number;
}

const CLIENT_FILTER_GRID_TYPES = new Set(['cases']);
const resolveFilterMode = (gridType: string): GridFeatureMode =>
  CLIENT_FILTER_GRID_TYPES.has(gridType) ? 'client' : 'server';

const DataGridTable = <T extends { id: string | number },>({
  gridType, rows, columns, page, pageSize, totalCount, paginationModeType,
  onPageChange, onPageSizeChange, onSearchChange,
  searchPlaceholder, itemTypeCountLabel, itemsPerPageLabel,
  filterOptions, selectedFilter, onFilterChange,
  legends, loading, actionButton, columnActionLabels, noRowsLabel, handleFilterChange, gaShieldToggle, selectionActions, addActionButton, onReload, isUserManagementLoaded, resetSearchKey,
}: CopDataGridProps<T>) => {
  const [searchValue, setSearchValue] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({ type: "include", ids: new Set() });
  const [pageInputValue, setPageInputValue] = useState<string>(String(page));
  const [isLoadingInternal, setIsLoadingInternal] = useState<boolean>(loading ?? false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const exportDropdownRef = useRef<HTMLDivElement>(null);
  const noRowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (noRowTimerRef.current) clearTimeout(noRowTimerRef.current);
    if (loading) {
      setIsLoadingInternal(true);
    } else if (rows.length > 0) {
      setIsLoadingInternal(false);
    } else {
      noRowTimerRef.current = setTimeout(() => setIsLoadingInternal(false), 2000);
    }
    return () => { if (noRowTimerRef.current) clearTimeout(noRowTimerRef.current); };
  }, [loading, rows]);

  useEffect(() => { setPageInputValue(String(page)); }, [page]);

  useEffect(() => { if (resetSearchKey) setSearchValue(''); }, [resetSearchKey]);

  const handleMouseDown = (e: MouseEvent) => {
    const target = e.target as Node;
    const clickedOutsideFilter = dropdownOpen && dropdownRef.current && !dropdownRef.current.contains(target);
    const clickedOutsideExport = exportDropdownOpen && exportDropdownRef.current && !exportDropdownRef.current.contains(target);
    if (clickedOutsideFilter) setDropdownOpen(false);
    if (clickedOutsideExport) setExportDropdownOpen(false);
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [dropdownOpen, exportDropdownOpen]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    onSearchChange?.(e.target.value);
  };

  const hasActionButton = actionButton?.type === 'action';
  const hasExportButton = actionButton?.type === 'export';
  const totalPages = Math.ceil(totalCount / pageSize) || 1;
  const handlePageInput = (val: string) => {
    const parsed = Number.parseInt(val, 10);
    const isValid = !Number.isNaN(parsed) && parsed >= 1 && parsed <= totalPages;
    if (isValid) onPageChange(parsed);
    else setPageInputValue(String(page));
  };

  const getEffectiveSelectedIds = (): Set<string | number> => {
    if (rowSelectionModel.type === 'exclude')
      return new Set(rows.map(r => r.id).filter(id => !rowSelectionModel.ids.has(id)));
    return rowSelectionModel.ids as Set<string | number>;
  };

  const getSelectedEmails = (): string[] => {
    const selectedIds = getEffectiveSelectedIds();
    const emails = rows
      .filter(row => selectedIds.has(row.id))
      .map(row => (row as T & { email?: unknown }).email)
      .filter((email): email is string | number => typeof email === 'string' || typeof email === 'number')
      .map(email => String(email).trim()).filter(Boolean);
    return Array.from(new Set(emails));
  };

  const openSendEmailModal = () => { emitSendEmailOpen({ email: getSelectedEmails() }); };

  return (
    <div className={`cop-data-grid${gridType === 'userManagement' ? ' cop-data-grid--user-management' : ''}`}>
      <DataGridActions
        searchValue={searchValue}
        searchPlaceholder={searchPlaceholder}
        handleSearchChange={handleSearchChange}
        onSearchChange={onSearchChange}
        setSearchValue={setSearchValue}
        filterOptions={filterOptions}
        selectedFilter={selectedFilter}
        dropdownOpen={dropdownOpen}
        setDropdownOpen={setDropdownOpen}
        onFilterChange={onFilterChange}
        dropdownRef={dropdownRef}
        actionButton={actionButton}
        exportDropdownOpen={exportDropdownOpen}
        setExportDropdownOpen={setExportDropdownOpen}
        exportDropdownRef={exportDropdownRef}
        hasActionButton={hasActionButton}
        hasExportButton={hasExportButton}
        gaShieldToggle={gaShieldToggle}
        addActionButton={addActionButton}
        gridType={gridType}
        hasNoRows={rows.length === 0}
      />
      {selectionActions?.enableMultiSelectUsers === true && !isLoadingInternal && rows.length > 0 && (
        <div className="cop-data-grid__selection-bar">
          <span className="cop-data-grid__selection-label">
            {selectionActions.label ?? 'Selected Users:'}&nbsp;
            <span className="cop-data-grid__selection-count">
                {(() => { const c = rowSelectionModel.type === 'exclude' ? totalCount - rowSelectionModel.ids.size : rowSelectionModel.ids.size; return c > 0 ? c : '-'; })()}
              </span>
          </span>
          <button
            type="button"
            className="cop-data-grid__selection-btn"
            aria-label="Delete selected"
            data-tooltip={
              getEffectiveSelectedIds().size === 0
                ? (selectionActions.noUsersSelectedLabel ?? 'No users selected')
                : (selectionActions.deleteSelectedUsersLabel ?? 'Delete Selected Users')
            }
            disabled={getEffectiveSelectedIds().size === 0}
            onClick={() => selectionActions.onDelete?.(Array.from(getEffectiveSelectedIds()))}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <polyline points="3 6 5 6 21 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            className="cop-data-grid__selection-btn"
            aria-label="Mail selected"
            data-tooltip={
              getEffectiveSelectedIds().size === 0
                  ? (selectionActions.noUsersSelectedLabel ?? 'No users selected')
                  : (selectionActions.notifySelectedUsersLabel ?? 'Notify Selected Users')
            }
            disabled={getEffectiveSelectedIds().size === 0}
            onClick={openSendEmailModal}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              <path d="M2 7l10 7 10-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )}
      {!isLoadingInternal && rows.length === 0 && gridType === 'userManagement' && isUserManagementLoaded && <NotFoundWidget onReload={onReload} />}
      <div className="cop-data-grid__table-wrap" style={!isLoadingInternal && rows.length === 0 && gridType === 'userManagement' && isUserManagementLoaded ? { display: 'none' } : undefined}>
        <DataGrid
          rows={rows} columns={columns} rowCount={totalCount}
          getRowHeight={(params) => {
            const hasMultiValue =
              params.model.customerEmailAddresses?.length > 1 ||
              params.model.siteIds?.length > 1;
            return hasMultiValue ? 'auto' : 52;
          }}
          disableColumnFilter={gridType === 'userManagement'}
          getRowClassName={(params) =>
            gridType === 'cases' && String(params.row.status ?? '').trim().toLowerCase() === 'my action'
              ? 'cop-row--my-action'
              : ''
          }
          filterMode={resolveFilterMode(gridType)}
          checkboxSelection={gridType === 'userManagement'}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelectionModel) => {
            setRowSelectionModel({
              ...newSelectionModel,
              ids: new Set(newSelectionModel.ids),
            });
            // Resolve 'exclude' models (e.g. "select all") to concrete ids before bubbling up
            const resolvedIds = newSelectionModel.type === 'exclude'
              ? rows.map(r => r.id).filter(id => !newSelectionModel.ids.has(id))
              : Array.from(newSelectionModel.ids);
            selectionActions?.onSelectionChange?.(resolvedIds);
          }}
          onFilterModelChange={handleFilterChange}
          pagination paginationMode={paginationModeType} paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(m) => { onPageChange(m.page + 1); onPageSizeChange(m.pageSize); }}
          pageSizeOptions={[5, 10, 25, 50]} disableRowSelectionOnClick disableColumnSelector={false}
          loading={isLoadingInternal} localeText={buildLocaleText(columnActionLabels, noRowsLabel)}
          slots={{ footer: CustomFooter, loadingOverlay: SpinnerOverlay }}
          slotProps={{ footer: { message: itemTypeCountLabel, count: totalCount } as CustomFooterProps }}
          sx={GRID_SX}
        />
      </div>
      <div className="cop-footer__bottom" style={!isLoadingInternal && rows.length === 0 && gridType === 'userManagement' && isUserManagementLoaded ? { display: 'none' } : undefined}>
        {legends && legends.length > 0
          ? <div className="cop-footer__legend">{legends.map((l) => <LegendDot key={`${l.color}-${l.label}`} color={l.color} label={l.label} />)}</div>
          : <div />}
        <div className="cop-footer__pagination">
          <span>{itemsPerPageLabel ?? 'Items per page'}</span>
          <select value={pageSize} onChange={e => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }} className="cop-footer__select">
            {[5, 10, 25, 50].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button type="button" onClick={() => onPageChange(page - 1)} disabled={page <= 1}
            className="cop-footer__nav-btn cop-footer__nav-btn--prev" aria-label="Previous page">
            <span className="cop-footer__nav-icon" aria-hidden="true" />
          </button>
          <span className="cop-footer__page-info">
            <input type="text" className="cop-footer__page-input" value={pageInputValue} aria-label="Page number"
              onChange={e => { if (e.target.value === '' || /^\d+$/.test(e.target.value)) setPageInputValue(e.target.value); }}
              onBlur={() => handlePageInput(pageInputValue)}
              onKeyDown={e => { if (e.key === 'Enter') { handlePageInput(pageInputValue); (e.target as HTMLInputElement).blur(); } }}
            />
            /{totalPages}
          </span>
          <button type="button" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}
            className="cop-footer__nav-btn cop-footer__nav-btn--next" aria-label="Next page">
            <span className="cop-footer__nav-icon" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};
export default DataGridTable;